import React from 'react';
import {
  Container,
  VStack,
  Box,
  SimpleGrid,
  Link,
  Icon,
  HStack,
  Image,
  Text,
  Heading,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import {
  FaEnvelope,
  FaWhatsapp,
  FaSpotify,
  FaPaypal,
  FaCopy,
  FaCheck,
} from 'react-icons/fa';
import { SiKofi, SiApplepodcasts, SiAmazon } from 'react-icons/si';
import SEO from '../components/SEO';
import ScrollIndicator from '../components/ScrollIndicator';
import PageHero from '../components/PageHero';
import ScrollReveal from '../components/ScrollReveal';

const UPI_ID = '9893547492@paytm';

const ContactLink = ({ icon, label, href, color = "brand.300", customIcon }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    _hover={{ textDecoration: 'none' }}
  >
    <HStack
      spacing={3}
      p={4}
      minH="56px"
      layerStyle="glassTile"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-2px)',
        borderColor: color,
        background: 'rgba(255,150,50,0.08)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
      }}
      align="center"
      justify="center"
    >
      {customIcon ? (
        <Box
          boxSize={{ base: 5, md: 6 }}
          borderRadius="sm"
          overflow="hidden"
          flexShrink={0}
        >
          <Image
            src={customIcon}
            alt={label}
            objectFit="contain"
            width="100%"
            height="100%"
          />
        </Box>
      ) : (
        <Icon as={icon} boxSize={{ base: 5, md: 6 }} color={color} />
      )}
      <Text
        fontSize={{ base: "sm", md: "md" }}
        color="whiteAlpha.900"
        fontWeight="medium"
        textAlign="center"
      >
        {label}
      </Text>
    </HStack>
  </Link>
);

const UpiTile = () => {
  const { hasCopied, onCopy } = useClipboard(UPI_ID);
  const toast = useToast();

  const handleCopy = () => {
    onCopy();
    toast({
      title: 'UPI ID copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <HStack
      spacing={3}
      p={4}
      minH="56px"
      layerStyle="glassTile"
      transition="all 0.2s"
      cursor="pointer"
      onClick={handleCopy}
      role="button"
      aria-label="Copy UPI ID"
      _hover={{
        transform: 'translateY(-2px)',
        borderColor: 'brand.300',
        background: 'rgba(255,150,50,0.08)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
      }}
      align="center"
      justify="center"
    >
      <Icon as={hasCopied ? FaCheck : FaCopy} boxSize={{ base: 4, md: 5 }} color={hasCopied ? 'green.400' : 'brand.300'} />
      <Text fontSize={{ base: "sm", md: "md" }} color="whiteAlpha.900" fontWeight="medium">
        UPI: {UPI_ID}
      </Text>
    </HStack>
  );
};

const ContactSection = ({ title, children }) => (
  <Box textAlign="center" width="100%">
    <Heading
      as="h3"
      fontSize={{ base: "xl", md: "2xl" }}
      mb={6}
      variant="gradient"
    >
      {title}
    </Heading>
    <VStack spacing={4} align="stretch">
      {children}
    </VStack>
  </Box>
);

const Contact = () => {
  return (
    <>
      <Box minH="100vh" pt={{ base: 20, md: 20 }} pb={{ base: 12, md: 20 }} id="contact-container">
        <SEO
          title="Contact Us - Namaskar India"
          description="Get in touch with us at Namaskar India podcast. Connect through social media, email, or support our work."
        />

        <PageHero
          title="Connect With Us"
          subtitle="Get in touch, follow us on social media, or support our work."
        />

        <Container maxW="container.xl" pt={{ base: 8, md: 12 }}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} px={{ base: 4, md: 0 }}>
            {/* Get in Touch Section */}
            <ScrollReveal delay={0}>
              <ContactSection title="Get in Touch">
                <ContactLink
                  icon={FaEnvelope}
                  href="mailto:contact.namaskarindia@gmail.com"
                  label="Email Us"
                />
                <ContactLink
                  icon={FaWhatsapp}
                  href="https://chat.whatsapp.com/H8IUJPlB32cA2soTjrTghV"
                  label="Join WhatsApp Group"
                  color="green.400"
                />
                <ContactLink
                  icon={FaWhatsapp}
                  href="https://chat.whatsapp.com/KVd5UHxumW90TxLHjkB89k"
                  label="WhatsApp Updates"
                  color="green.400"
                />
              </ContactSection>
            </ScrollReveal>

            {/* Listen & Follow Section */}
            <ScrollReveal delay={0.1}>
              <ContactSection title="Listen & Follow">
                <SimpleGrid columns={2} spacing={4}>
                  <ContactLink
                    icon={FaSpotify}
                    href="https://open.spotify.com/show/03UrDnDHONZ92oOpWzfhxx"
                    label="Spotify"
                    color="#1DB954"
                  />
                  <ContactLink
                    icon={SiApplepodcasts}
                    href="https://podcasts.apple.com/us/podcast/namaskar-india-tales-from-indian-history-and-mythology/id1516915527"
                    label="Apple"
                    color="#B150E2"
                  />
                  <ContactLink
                    icon={SiAmazon}
                    href="https://music.amazon.com/podcasts/3b2ce8d8-2c19-4b40-a9f3-3671d7e6ce82"
                    label="Amazon Music"
                    color="#00A8E1"
                  />
                  <ContactLink
                    customIcon="/gaana-logo.png"
                    href="https://gaana.com/podcast/namaskar-india-season-1"
                    label="Gaana"
                  />
                  <ContactLink
                    customIcon="/jiosaavn-logo.png"
                    href="https://www.jiosaavn.com/shows/namaskar-india/1/rj-QzdPFEEk_"
                    label="JioSaavn"
                  />
                </SimpleGrid>
              </ContactSection>
            </ScrollReveal>

            {/* Support Section */}
            <ScrollReveal delay={0.2}>
              <ContactSection title="Support Us">
                <ContactLink
                  icon={SiKofi}
                  href="http://ko-fi.com/namaskarindia"
                  label="Support on Ko-fi"
                  color="#FF5E5B"
                />
                <ContactLink
                  icon={FaPaypal}
                  href="https://www.paypal.com/paypalme/aduppala"
                  label="PayPal"
                  color="#00457C"
                />
                <UpiTile />
              </ContactSection>
            </ScrollReveal>
          </SimpleGrid>
        </Container>
        <ScrollIndicator containerId="contact-container" />
      </Box>
    </>
  );
};

export default Contact;
